import React, { useState } from "react";
import { X, KeyRound, Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/admin/api";

const rules = [
  { key: "len", label: "At least 10 characters", test: (v) => v.length >= 10 },
  { key: "upper", label: "One uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { key: "lower", label: "One lowercase letter", test: (v) => /[a-z]/.test(v) },
  { key: "num", label: "One number", test: (v) => /\d/.test(v) },
];

export default function ChangePasswordModal({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const passesAll = rules.every((r) => r.test(next));
  const matches = next && confirm && next === confirm;
  const canSubmit = current && passesAll && matches && !submitting;

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm("");
    setShowCurrent(false); setShowNext(false);
  };

  const close = () => { if (!submitting) { reset(); onClose(); } };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated successfully");
      reset();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update password");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" data-testid="change-password-modal">
      <div className="absolute inset-0 bg-navy/70 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-md bg-white border border-slate-200 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-navy text-gold flex items-center justify-center">
              <KeyRound className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div>
              <span className="overline text-gold text-[10px]">Account Security</span>
              <h2 className="font-display font-800 text-navy text-lg tracking-tightest leading-tight">Change Password</h2>
            </div>
          </div>
          <button onClick={close} data-testid="change-password-close" className="p-2 text-slate-400 hover:text-navy" disabled={submitting}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-600 text-navy/60 uppercase tracking-wider mb-2">Current password</label>
            <div className="relative">
              <input
                type={showCurrent ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="current-password-input"
                className="w-full bg-white border border-slate-300 px-3 py-2.5 pr-10 text-sm text-navy outline-none focus:ring-2 focus:ring-gold"
              />
              <button type="button" onClick={() => setShowCurrent((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-navy" tabIndex={-1}>
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-600 text-navy/60 uppercase tracking-wider mb-2">New password</label>
            <div className="relative">
              <input
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="new-password-input"
                className="w-full bg-white border border-slate-300 px-3 py-2.5 pr-10 text-sm text-navy outline-none focus:ring-2 focus:ring-gold"
              />
              <button type="button" onClick={() => setShowNext((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-navy" tabIndex={-1}>
                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <ul className="mt-3 grid grid-cols-2 gap-y-1.5 gap-x-3">
              {rules.map((r) => {
                const ok = r.test(next);
                return (
                  <li key={r.key} className={`flex items-center gap-1.5 text-xs ${ok ? "text-emerald-600" : "text-slate-400"}`}>
                    <Check className={`w-3.5 h-3.5 ${ok ? "opacity-100" : "opacity-30"}`} strokeWidth={2.5} />
                    <span>{r.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <label className="block text-xs font-600 text-navy/60 uppercase tracking-wider mb-2">Confirm new password</label>
            <input
              type={showNext ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              data-testid="confirm-password-input"
              className="w-full bg-white border border-slate-300 px-3 py-2.5 text-sm text-navy outline-none focus:ring-2 focus:ring-gold"
            />
            {confirm && !matches && (
              <p className="mt-1.5 text-xs text-rose-600" data-testid="password-mismatch">Passwords don&apos;t match.</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button" onClick={close} disabled={submitting}
              data-testid="change-password-cancel"
              className="px-4 py-2.5 text-sm font-600 text-navy hover:bg-slate-100 disabled:opacity-50"
            >Cancel</button>
            <button
              type="submit" disabled={!canSubmit}
              data-testid="change-password-submit"
              className="px-5 py-2.5 text-sm font-700 bg-navy text-white hover:bg-navy/90 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
            >{submitting ? "Updating..." : "Update Password"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
